import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function ProcessSection() {
  const steps = [
    {
      number: '01',
      phase: 'Phase 1',
      duration: '1-2 jours',
      icon: 'ri-phone-line',
      title: 'Contact et audit',
      description: 'Prise de contact, visite sur site et réalisation de l\'audit énergétique gratuit',
      color: 'from-emerald-500 to-teal-600',
    },
    {
      number: '02',
      phase: 'Phase 2',
      duration: '5-7 jours',
      icon: 'ri-file-text-line',
      title: 'Étude et devis',
      description: 'Réalisation de l\'étude photométrique, constitution du dossier CEE et devis détaillé',
      color: 'from-teal-500 to-cyan-600',
    },
    {
      number: '03',
      phase: 'Phase 3',
      duration: '2-3 semaines',
      icon: 'ri-checkbox-circle-line',
      title: 'Validation et commande',
      description: 'Validation du projet, signature du devis et commande des équipements LED',
      color: 'from-cyan-500 to-blue-600',
    },
    {
      number: '04',
      phase: 'Phase 4',
      duration: '1-2 jours',
      icon: 'ri-tools-line',
      title: 'Installation',
      description: 'Installation des luminaires LED par nos techniciens certifiés et mise en service',
      color: 'from-blue-500 to-indigo-600',
    },
    {
      number: '05',
      phase: 'Phase 5',
      duration: '1 jour',
      icon: 'ri-award-line',
      title: 'Finalisation',
      description: 'Tests de fonctionnement, formation utilisateur et remise de la garantie 5 ans',
      color: 'from-indigo-500 to-purple-600',
    },
  ];

  return (
    <section className="relative py-8 sm:py-12 lg:py-16 bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden">
      {/* Animated Background - Desktop only */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={window.innerWidth >= 768 ? {
            rotate: [0, 360],
            scale: [1, 1.5, 1],
            opacity: [0.02, 0.06, 0.02],
          } : {}}
          transition={window.innerWidth >= 768 ? { duration: 30, repeat: Infinity, ease: "linear" } : {}}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] sm:w-[650px] lg:w-[800px] h-[500px] sm:h-[650px] lg:h-[800px] bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full blur-3xl"
          style={{ opacity: window.innerWidth >= 768 ? undefined : 0.02 }}
        ></motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-full px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 mb-3 sm:mb-4"
          >
            <motion.i 
              className="ri-route-line text-emerald-600 text-xs sm:text-sm lg:text-base"
              animate={window.innerWidth >= 768 ? { 
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              } : {}}
              transition={window.innerWidth >= 768 ? { 
                duration: 3,
                repeat: Infinity,
                ease: "linear"
              } : {}}
            ></motion.i>
            <span className="text-[10px] sm:text-xs lg:text-sm font-bold text-emerald-600 uppercase tracking-wider">
              Notre Processus
            </span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-3 sm:mb-4 lg:mb-6"
          >
            5 Étapes Simples
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xs sm:text-base md:text-lg lg:text-xl text-slate-600 max-w-2xl mx-auto"
          >
            Un parcours fluide et transparent pour votre projet de rénovation LED
          </motion.p>
        </div>

        {/* Timeline Desktop */}
        <div className="hidden lg:block relative">
          <motion.div
            className="absolute top-16 sm:top-20 lg:top-24 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 via-cyan-500 via-blue-500 to-purple-500"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 2, delay: 0.5 }}
          ></motion.div>

          <div className="grid grid-cols-5 gap-4 lg:gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                <motion.div
                  whileHover={window.innerWidth >= 768 ? { y: -10, scale: 1.05 } : {}}
                  className="group relative bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all cursor-pointer overflow-hidden"
                >
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                    animate={window.innerWidth >= 768 ? {
                      backgroundPosition: ['0% 0%', '100% 100%'],
                    } : {}}
                    transition={window.innerWidth >= 768 ? { duration: 5, repeat: Infinity, repeatType: "reverse" } : {}}
                  ></motion.div>

                  <motion.div
                    className={`relative w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-4 sm:mb-5 lg:mb-6 mx-auto`}
                    whileHover={window.innerWidth >= 768 ? { rotate: 360, scale: 1.2 } : {}}
                    transition={window.innerWidth >= 768 ? { duration: 0.6 } : {}}
                  >
                    <motion.span 
                      className="text-2xl sm:text-2xl lg:text-3xl font-bold text-white"
                      animate={window.innerWidth >= 768 ? { 
                        scale: [1, 1.1, 1],
                      } : {}}
                      transition={window.innerWidth >= 768 ? { duration: 2, repeat: Infinity, delay: index * 0.3 } : {}}
                    >
                      {step.number}
                    </motion.span>
                  </motion.div>

                  <motion.div
                    className="relative w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-lg sm:rounded-xl bg-slate-100 flex items-center justify-center mb-3 sm:mb-3.5 lg:mb-4 mx-auto"
                    whileHover={window.innerWidth >= 768 ? { rotate: 360 } : {}}
                    transition={window.innerWidth >= 768 ? { duration: 0.5 } : {}}
                  >
                    <motion.i 
                      className={`${step.icon} text-xl sm:text-xl lg:text-2xl bg-gradient-to-br ${step.color} bg-clip-text text-transparent`}
                      animate={window.innerWidth >= 768 ? { 
                        scale: [1, 1.2, 1],
                      } : {}}
                      transition={window.innerWidth >= 768 ? { duration: 2, repeat: Infinity, delay: index * 0.4 } : {}}
                    ></motion.i>
                  </motion.div>

                  <div className="text-center space-y-1 lg:space-y-2">
                    <div className="text-xs text-emerald-600 font-semibold">{step.phase}</div>
                    <div className="text-xs text-slate-500">{step.duration}</div>
                    <h3 className="relative text-base sm:text-lg lg:text-xl font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                      {step.title}
                    </h3>
                    <p className="relative text-xs sm:text-sm text-slate-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  <motion.div
                    className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${step.color}`}
                    initial={{ width: '0%' }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                  ></motion.div>

                  <motion.div
                    className={`absolute -bottom-10 -right-10 w-24 sm:w-28 lg:w-32 h-24 sm:h-28 lg:h-32 bg-gradient-to-br ${step.color} rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500`}
                    animate={window.innerWidth >= 768 ? {
                      scale: [1, 1.5, 1],
                      rotate: [0, 180, 360],
                    } : {}}
                    transition={window.innerWidth >= 768 ? { duration: 10, repeat: Infinity } : {}}
                  ></motion.div>

                  <motion.div
                    className={`absolute top-3 sm:top-4 right-3 sm:right-4 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-gradient-to-br ${step.color} rounded-full`}
                    animate={window.innerWidth >= 768 ? {
                      y: [0, -10, 0],
                      opacity: [0.3, 1, 0.3],
                    } : {}}
                    transition={window.innerWidth >= 768 ? { duration: 3, repeat: Infinity, delay: index * 0.5 } : {}}
                  ></motion.div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Timeline Mobile - Version statique sans animations */}
        <div className="lg:hidden space-y-3">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative"
            >
              {index < steps.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-6 bg-gradient-to-b from-emerald-200 to-teal-200"></div>
              )}
              
              <div className="flex items-start space-x-4">
                <div className="relative flex-shrink-0">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                    <i className={`${step.icon} text-white text-lg`}></i>
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full border-2 border-emerald-500 flex items-center justify-center">
                    <span className="text-[8px] font-bold text-emerald-600">{index + 1}</span>
                  </div>
                </div>

                <div className="flex-1 bg-white rounded-lg p-3 shadow-sm border border-slate-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-emerald-600 font-semibold">{step.phase}</span>
                    <span className="text-[10px] text-slate-500">{step.duration}</span>
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 mb-1">{step.title}</h3>
                  <p className="text-[11px] text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="text-center mt-6 sm:mt-8 lg:mt-12"
        >
          <motion.div
            whileHover={window.innerWidth >= 768 ? { scale: 1.05 } : {}}
            whileTap={window.innerWidth >= 768 ? { scale: 0.95 } : {}}
          >
            <Link
              to="/nos-services"
              className="group inline-flex items-center gap-1.5 sm:gap-2 lg:gap-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-5 sm:px-7 lg:px-8 py-2.5 sm:py-3.5 lg:py-4 rounded-xl sm:rounded-2xl text-xs sm:text-base lg:text-lg font-bold hover:shadow-2xl hover:shadow-emerald-500/30 transition-all cursor-pointer whitespace-nowrap"
            >
              <motion.i 
                className="ri-service-line text-sm sm:text-lg lg:text-xl"
                animate={window.innerWidth >= 768 ? { rotate: [0, 360] } : {}}
                transition={window.innerWidth >= 768 ? { duration: 3, repeat: Infinity } : {}}
              ></motion.i>
              Découvrir tous nos services
              <motion.i 
                className="ri-arrow-right-line text-sm sm:text-lg lg:text-xl group-hover:translate-x-1 transition-transform"
              ></motion.i>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
